import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { User, Mail, Phone, GraduationCap, MapPin, Calendar, Edit } from 'lucide-react';

const StudentProfile = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);

  return (
    <DashboardLayout title="My Profile">
      <div className="max-w-3xl mx-auto">
        {/* Profile Header */}
        <Card className="mb-6" data-testid="profile-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-12 h-12 text-primary" />
              </div>
              <div className="text-center md:text-left flex-1">
                <h2 className="font-heading text-2xl font-bold">{user?.name}</h2>
                <p className="text-muted-foreground">{user?.email}</p>
                <div className="flex flex-wrap gap-2 mt-2 justify-center md:justify-start">
                  <Badge>{user?.role}</Badge>
                  {user?.qualification && <Badge variant="secondary">{user.qualification}</Badge>}
                </div>
              </div>
              <Button variant="outline" onClick={() => setEditing(!editing)} data-testid="edit-profile-btn">
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Profile Details */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <User className="w-4 h-4" /> Full Name
                </Label>
                {editing ? (
                  <Input defaultValue={user?.name} data-testid="edit-name" />
                ) : (
                  <p className="font-medium">{user?.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email Address
                </Label>
                <p className="font-medium">{user?.email}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Phone Number
                </Label>
                {editing ? (
                  <Input defaultValue={user?.phone} data-testid="edit-phone" />
                ) : (
                  <p className="font-medium">{user?.phone || 'Not provided'}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" /> Qualification
                </Label>
                <p className="font-medium">{user?.qualification || 'Not provided'}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Preferred Center
                </Label>
                <p className="font-medium">{user?.preferred_center || 'Not selected'}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Member Since
                </Label>
                <p className="font-medium">{user?.created_at?.split('T')[0] || 'N/A'}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="text-muted-foreground">Referral Code</Label>
              <div className="flex items-center gap-2">
                <code className="bg-muted px-4 py-2 rounded-lg font-mono text-lg font-bold">
                  {user?.referral_code || 'KOTA1234'}
                </code>
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(user?.referral_code || '');
                  toast.success('Copied!');
                }}>
                  Copy
                </Button>
              </div>
            </div>

            {editing && (
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={() => {
                  toast.success('Profile updated successfully!');
                  setEditing(false);
                }} data-testid="save-profile-btn">
                  Save Changes
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StudentProfile;
